import { useCallback, useMemo, useState } from 'react';
import { type LocationServiceLocationItem, type LocationServiceValidLocation } from '@frontend/wam-location-service';
import { useLocalStorage } from '@frontend/web-storage';
import { useModalControl } from '@frontend/design-system';
import { searchLocations as searchTrueLarkLocations, TRUELARK_LOCATION_HISTORY_LIMIT } from '../index';

const TRUELARK_LOCATION_HISTORY_KEY = 'wam-truelark__location-history';

export function useTrueLarkLocation(userId: string | undefined) {
  const [recentLocations, setRecentLocations] = useLocalStorage<LocationServiceLocationItem[]>({
    key: `${TRUELARK_LOCATION_HISTORY_KEY}:${userId}`,
    defaultValue: [],
  });

  const addLocation = useCallback(
    ({ locationId, name = locationId, domain, businessType }: LocationServiceLocationItem) => {
      const existing = recentLocations ?? [];
      if (existing[0]?.locationId === locationId) return;
      setRecentLocations(
        [{ locationId, name, domain, businessType }, ...existing.filter((l) => l.locationId !== locationId)].slice(
          0,
          TRUELARK_LOCATION_HISTORY_LIMIT
        )
      );
    },
    [recentLocations, setRecentLocations]
  );

  const [selectedBusiness, setSelectedBusiness] = useState<LocationServiceLocationItem | null>(null);
  const { modalProps: locationTrayProps, triggerProps: locationTriggerProps } = useModalControl();
  const { onClose: closeTray } = locationTrayProps;

  const searchLocations = useCallback(async (query: string): Promise<LocationServiceValidLocation[]> => {
    const { businesses } = await searchTrueLarkLocations(query);
    return (businesses ?? []).map((b) => ({
      locationId: b.tlBusinessId,
      name: b.name,
      domain: b.domain,
      businessType: b.businessType,
    })) as LocationServiceValidLocation[];
  }, []);

  const onChangeLocation = useCallback(
    (location: LocationServiceLocationItem) => {
      setSelectedBusiness(location);
      addLocation(location);
      closeTray();
    },
    [addLocation, closeTray]
  );

  const itemSections = useMemo(
    () => [{ heading: 'Recently Visited', items: recentLocations ?? [] }],
    [recentLocations]
  );

  return {
    selectedBusiness,
    locationTrayProps,
    locationTriggerProps,
    searchLocations,
    onChangeLocation,
    itemSections,
  };
}
